import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import './LandingPage.css';
import { CHARACTERS } from '../assets/characters';

const PROBLEMS = [
  {
    pain: 'Avant',
    title: 'Obligations ratées',
    text: 'Seuils d’effectif, CSE, DUERP, index égalité… noyés dans des tableurs et des mails.',
  },
  {
    pain: 'Avant',
    title: 'Audits stressants',
    text: 'On découvre les écarts au dernier moment, juste avant un contrôle ou une échéance.',
  },
  {
    pain: 'Avant',
    title: 'Réponses trop lentes',
    text: 'Chaque doute juridique = recherche longue ou appel à un cabinet.',
  },
];

const SOLUTIONS = [
  {
    label: 'Avec JULIA',
    title: 'Obligations sous contrôle',
    text: 'Alertes avant échéance, adaptées à votre effectif et à votre structure.',
  },
  {
    label: 'Avec JULIA',
    title: 'Conformité lisible',
    text: 'Un audit RH clair, des scores et un plan d’action immédiatement actionnable.',
  },
  {
    label: 'Avec JULIA',
    title: 'Réponses contextualisées',
    text: 'L’IA JULIA répond dans le cadre de votre entreprise, avec les sources consultées.',
  },
];

const COMPANY_LOGOS = [
  { src: '/landing/logo-atelier-nord.webp', alt: 'Atelier Nord' },
  { src: '/landing/logo-maison-lenoir.webp', alt: 'Maison Lenoir' },
  { src: '/landing/logo-groupe-alize.webp', alt: 'Groupe Alizé' },
  { src: '/landing/logo-clinique-horizon.webp', alt: 'Clinique Horizon' },
  { src: '/landing/logo-logitrans.webp', alt: 'LogiTrans' },
  { src: '/landing/logo-retailco.webp', alt: 'Retail & Co' },
  { src: '/landing/logo-techmaine.webp', alt: 'TechMaine' },
  { src: '/landing/logo-agro-valois.webp', alt: 'Agro Valois' },
];

const HERO_VIDEOS = [
  '/landing/hero.mp4',
  '/landing/hero-b.mp4',
  '/landing/hero-c.mp4',
  '/landing/hero-d.mp4',
  '/landing/hero-e.mp4',
  '/landing/hero-f.mp4',
];

/** Durée d'affichage d'un clip (clips Veo = 8s) : on change avant la fin pour éviter un rewind. */
const HERO_SLIDE_MS = 7500;
const HERO_FADE_MS = 1400;

const LandingPage: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeHero, setActiveHero] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || HERO_VIDEOS.length < 2) return undefined;

    const timer = window.setInterval(() => {
      setActiveHero((prev) => (prev + 1) % HERO_VIDEOS.length);
    }, HERO_SLIDE_MS);

    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const videos = Array.from(
      document.querySelectorAll<HTMLVideoElement>('.lp-hero-video'),
    );
    const nextIndex = (activeHero + 1) % HERO_VIDEOS.length;

    videos.forEach((video, index) => {
      video.muted = true;
      video.loop = false;
      video.playsInline = true;

      if (index === activeHero) {
        // Chaque slide repart du début : pas de reprise au milieu / pas de double boucle
        try {
          video.currentTime = 0;
        } catch {
          /* ignore si pas encore prêt */
        }
        void video.play().catch(() => undefined);
        return;
      }

      video.pause();
      if (index === nextIndex) {
        // Préparer la suivante au frame 0, sans la lire (évite d'arriver en fin de clip)
        try {
          video.currentTime = 0;
        } catch {
          /* ignore */
        }
      }
    });
  }, [activeHero]);

  return (
    <div className="lp">
      <header className={`lp-nav ${scrolled ? 'lp-nav--solid' : ''}`}>
        <Link to="/" className="lp-brand" aria-label="JULIA, accueil">
          <span className="lp-brand-mark">J</span>
          <span className="lp-brand-name">
            JU&apos;LIA <em>IA</em>
          </span>
        </Link>
        <div className="lp-nav-actions">
          <Link to="/login" className="lp-btn lp-btn--ghost">
            Se connecter
          </Link>
          <Link to="/register" className="lp-btn lp-btn--primary">
            S&apos;inscrire
          </Link>
        </div>
      </header>

      <section className="lp-hero" aria-label="Présentation">
        <div className="lp-hero-bg" aria-hidden="true">
          <div
            className="lp-hero-media"
            style={{ ['--lp-hero-fade' as string]: `${HERO_FADE_MS}ms` }}
          >
            {HERO_VIDEOS.map((src, index) => (
              <video
                key={src}
                className={`lp-hero-video${index === activeHero ? ' is-active' : ''}`}
                muted
                playsInline
                preload={index <= 1 ? 'auto' : 'metadata'}
                poster="/landing/hero.jpg"
              >
                <source src={src} type="video/mp4" />
              </video>
            ))}
          </div>
          <div className="lp-hero-overlay" />
        </div>
        <motion.div
          className="lp-hero-inner"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="lp-eyebrow">Plateforme RH et droit du travail, France</p>
          <h1 className="lp-hero-title">
            La conformité RH,
            <span className="lp-hero-title-accent"> enfin sous contrôle.</span>
          </h1>
          <p className="lp-hero-lead">
            JULIA aide les dirigeants et les RH à anticiper les obligations légales,
            piloter la conformité et répondre vite aux exigences des collaborateurs,
            évaluer le bonheur et le bien-être dans votre entreprise.
          </p>
          <div className="lp-hero-cta">
            <Link to="/register" className="lp-btn lp-btn--accent lp-btn--lg">
              Essayer gratuitement
            </Link>
            <a href="#valeur" className="lp-btn lp-btn--soft lp-btn--lg">
              Voir ce que ça change
            </a>
            <a
              href="mailto:contact@julia-rh.com?subject=Rendez-vous%20avec%20un%20expert%20JULIA"
              className="lp-btn lp-btn--soft lp-btn--lg"
            >
              Prendre un rendez-vous avec nos experts
            </a>
          </div>
          <p className="lp-hero-note">Le futur de votre SIRH est ici</p>
        </motion.div>
      </section>

      <section className="lp-logos" aria-label="Entreprises qui nous font confiance">
        <p className="lp-logos-label">
          Conçue pour les PME et ETI qui portent la responsabilité RH au quotidien
        </p>
        <div className="lp-logos-track-wrap">
          <div className="lp-logos-track">
            {[...COMPANY_LOGOS, ...COMPANY_LOGOS].map((logo, i) => (
              <div key={`${logo.alt}-${i}`} className="lp-logo-item">
                <img src={logo.src} alt={logo.alt} loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="lp-section" id="valeur">
        <div className="lp-section-head">
          <p className="lp-eyebrow lp-eyebrow--dark">Le problème</p>
          <h2 className="lp-h2">Ce qui freine vraiment les équipes RH</h2>
          <p className="lp-lead">
            Pas un outil de plus : une réponse claire aux risques que vous vivez déjà.
          </p>
        </div>
        <div className="lp-grid-3">
          {PROBLEMS.map((item, index) => (
            <motion.article
              key={item.title}
              className="lp-tile lp-tile--pain"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.4, delay: index * 0.07 }}
            >
              <span className="lp-tile-tag">{item.pain}</span>
              <h3 className="lp-h3">{item.title}</h3>
              <p className="lp-body">{item.text}</p>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="lp-showcase" aria-label="Aperçu produit">
        <div className="lp-showcase-inner">
          <div className="lp-showcase-copy">
            <p className="lp-eyebrow lp-eyebrow--dark">Dans l’application</p>
            <h2 className="lp-h2">Un cockpit RH clair, un CRM à votre image</h2>
            <p className="lp-lead">
              Obligations, audit, collaborateurs et documents au même endroit.
              JULIA vous montre où vous en êtes, et quoi faire ensuite.
              Des experts à votre service.
            </p>
            <Link to="/register" className="lp-btn lp-btn--navy lp-btn--lg">
              Créer mon espace
            </Link>
          </div>
          <motion.div
            className="lp-showcase-media"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.55 }}
          >
            <img
              src="/landing/visual-audit.jpg"
              alt="Aperçu du tableau de bord de conformité JULIA"
              loading="lazy"
            />
            <img
              className="lp-showcase-human"
              src={CHARACTERS.landing}
              alt=""
              aria-hidden
            />
          </motion.div>
        </div>
      </section>

      <section className="lp-section lp-section--tint">
        <div className="lp-section-head">
          <p className="lp-eyebrow lp-eyebrow--dark">La solution</p>
          <h2 className="lp-h2">Ce que JULIA change concrètement</h2>
          <p className="lp-lead">
            Une plateforme unique : obligations, audit, collaborateurs… et une IA qui agit dans votre contexte.
          </p>
        </div>
        <div className="lp-grid-3">
          {SOLUTIONS.map((item, index) => (
            <motion.article
              key={item.title}
              className="lp-tile lp-tile--gain"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.4, delay: index * 0.07 }}
            >
              <span className="lp-tile-tag lp-tile-tag--ok">{item.label}</span>
              <h3 className="lp-h3">{item.title}</h3>
              <p className="lp-body">{item.text}</p>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="lp-cta-band" aria-label="Essai gratuit">
        <motion.div
          className="lp-cta-band-inner"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
        >
          <h2 className="lp-cta-title">Prêt à clarifier votre conformité RH ?</h2>
          <p className="lp-cta-text">
            Créez l’espace de votre entreprise en quelques minutes. Aucune carte bancaire pour démarrer.
          </p>
          <Link to="/register" className="lp-btn lp-btn--accent lp-btn--lg">
            Essayer gratuitement
          </Link>
        </motion.div>
      </section>

      <footer className="lp-footer">
        <div className="lp-footer-brand">
          <span className="lp-brand-mark lp-brand-mark--sm">J</span>
          <strong>JU&apos;LIA</strong>
        </div>
        <p className="lp-footer-copy">Plateforme SaaS RH et juridique, France</p>
        <div className="lp-footer-links">
          <Link to="/login">Se connecter</Link>
          <Link to="/register">S&apos;inscrire</Link>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
